import styled from "@emotion/styled";

import { fontMerriweather, fontRoboto, fontRobotoCondensed, theme } from "shared/lib/styles";

import { AuthorEntityLink } from "scmp-app/components/content/content-author/styles";
import { H1Container, H2Container } from "scmp-app/components/content/content-headline/styles";
import { StyledGalleryIndicator } from "scmp-app/components/content/content-item-render/styles";
import {
  StyledMessage as PrintHeadlineMessage,
  StyledHeadline as PrintHeadlineText,
} from "scmp-app/components/content/content-print-headline/styles";
import { ContentPublishedDate } from "scmp-app/components/content/content-published-date";
import { ContentSections } from "scmp-app/components/content/content-sections";
import { EntityLink } from "scmp-app/components/entity-link";
import ImagePlusFlag from "scmp-app/components/plus/plus-flag/img-plus.svg";
import { Icon as VideoDurationOverlayIcon } from "scmp-app/components/video/video-duration-overlay/variants/homepage/styles";

export const StyledEntityLink = styled(EntityLink)`
  inline-size: 100%;
`;

export const StyledPublishedDate = styled(ContentPublishedDate)`
  grid-area: date;

  display: flex;
  align-items: center;

  color: #727272;
  font-weight: 400;
  font-size: 11px;
  font-family: ${fontRoboto};
  line-height: normal;
`;

export const Container = styled.div`
  display: grid;
  grid:
    "date date" min-content
    ". . " 12px
    "header cover-image" min-content
    "headline cover-image" min-content
    "author cover-image" min-content
    "print-headline print-headline" min-content
    / minmax(0, auto) 100px;
  column-gap: 12px;

  ${theme.breakpoints.up("tablet")} {
    grid:
      "date header cover-image" min-content
      ". headline cover-image" min-content
      ". summary cover-image" min-content
      ". author cover-image" min-content
      ". print-headline cover-image" min-content
      / minmax(0, auto) 1fr 216px;
    column-gap: 32px;
  }
`;

export const Header = styled.div`
  grid-area: header;

  display: flex;
  gap: 8px;
  align-items: center;
`;

export const StyledSections = styled(ContentSections)`
  margin: 0;

  a {
    color: #333333;
    font-weight: 500;
    font-size: 12px;
    font-family: ${fontRobotoCondensed};
    line-height: normal;
    text-transform: uppercase;
  }

  span {
    color: inherit;
    font: inherit;
  }
`;

export const PlusFlag = styled(ImagePlusFlag)`
  display: block;
`;

export const Headline = styled.div`
  grid-area: headline;

  margin-block-start: 4px;

  ${H1Container}, ${H2Container} {
    color: #000000;
    font-weight: 700;
    font-size: 14px;
    font-family: ${fontMerriweather};
    line-height: 1.4;

    ${theme.breakpoints.up("tablet")} {
      font-size: 17px;
    }
  }
`;

export const Summary = styled.div`
  grid-area: summary;

  margin-block-start: 4px;

  ${StyledEntityLink} {
    max-block-size: 58.5px;

    color: #6f6f6f;
    font-weight: 400;
    font-size: 13px;
    font-family: ${fontMerriweather};
    line-height: 1.5;

    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }

  &:has(${StyledEntityLink}:empty) {
    display: none;
  }

  ${theme.breakpoints.only("mobile")} {
    display: none;
  }
`;

export const Author = styled.div`
  grid-area: author;

  display: flex;
  flex-flow: row wrap;
  align-items: center;

  margin-block-start: 4px;

  &:empty {
    display: none;
  }

  ${AuthorEntityLink} {
    color: #5a5a5a;
    font-weight: 700;
    font-size: 12px;
    font-family: ${fontRoboto};
  }
`;

export const PrintHeadlineHeader = styled.span`
  display: inline-block;

  padding-block-start: 8px;

  color: #727272;
  font-weight: 400;
  font-size: 10px;
  font-family: ${fontRoboto};
  border-block-start: 1px #dfdfdf solid;
`;

export const PrintHeadline = styled.div`
  color: #333333;
  font-size: 12px;
  font-family: ${fontMerriweather};
  line-height: normal;

  ${PrintHeadlineMessage} {
    display: none;
  }

  ${PrintHeadlineText} {
    color: #333333;
    font-weight: 400;
    font-family: ${fontMerriweather};
  }
`;

export const PrintHeadlineContainer = styled.div`
  grid-area: print-headline;

  margin-block-start: 12px;

  :has(${PrintHeadline}:empty) {
    display: none;
  }
`;

export const CoverImage = styled.div`
  grid-area: cover-image;

  position: relative;

  align-self: start;

  aspect-ratio: 1 / 1;

  inline-size: 100px;

  ${theme.breakpoints.up("tablet")} {
    aspect-ratio: 3 / 2;

    inline-size: 216px;
  }

  figure {
    block-size: 100%;
    margin: 0;
  }

  img {
    inline-size: 100%;
    block-size: 100%;

    object-fit: cover;
  }

  ${VideoDurationOverlayIcon} {
    display: none;

    ${theme.breakpoints.up("tablet")} {
      display: block;
    }
  }

  ${StyledGalleryIndicator} {
    display: none;

    ${theme.breakpoints.up("tablet")} {
      display: flex;
    }
  }
`;
